import { gray } from "chalk";

export type StackFrame = {
  raw: string;           // original line
  fn?: string;           // function or method name
  file?: string;         // full file path (if available)
  shortFile?: string;    // file name with no path
  line?: number;         // 1-based
  column?: number;       // 1-based
};

export type BreadcrumbOptions = {
  /** Max frames to include after filtering. */
  maxDepth?: number;
  /** Remove consecutive duplicate frames (same fn+file+line). */
  dedupeConsecutive?: boolean;
  /** Ignore patterns for filtering out frames by raw line match. */
  ignorePatterns?: (string | RegExp)[];
  /** If true, filter common “internal” frames even if ignorePatterns not provided. */
  filterCommonInternals?: boolean;
  /** If true, include line:col in breadcrumb text. */
  includeLocationInText?: boolean;
};

const DEFAULT_IGNORE: (string | RegExp)[] = [
  /index\.js$/i,              // entrypoint
  /node:internal/i,           // node internals (v18+)
  /(?:^|\b)internal\/.+/i,    // older node internal frames
  /node_modules[\\/]/i,       // deps
  /\/dist[\\/]/i,             // built outputs
];

function shortFilename(pathOrUrl: string) {
  // strip query/hash (browser stacks), keep last segment
  const noQuery = pathOrUrl.split(/[?#]/)[0];
  return noQuery.replace(/^.*[\\/]/, "");
}

function parseFrame(line: string): StackFrame {
  // Typical patterns:
  // "at fnName (path/file.ts:10:5)"
  // "at path/file.ts:10:5"
  // "at Object.fn [as x] (path/file.js:1:1)"
  const trimmed = line.trim();
  const atMatch = trimmed.startsWith("at ") ? trimmed.slice(3) : trimmed;

  // Try to split "fn (loc)" vs "loc"
  const matchRegExp = /^(.*)\s+\((.*)\)$/;
  const m = matchRegExp.exec(atMatch);
  let fn: string | undefined;
  let loc = atMatch;

  if (m) {
    fn = m[1]?.trim();
    loc = m[2]?.trim();
  }

  // loc may be "file:line:col"
  const locRegex = /^(.*?):(\d+):(\d+)$/;
  const locMatch = locRegex.exec(loc);
  let file: string | undefined;
  let lineNum: number | undefined;
  let colNum: number | undefined;

  if (locMatch) {
    file = locMatch[1];
    lineNum = Number(locMatch[2]);
    colNum = Number(locMatch[3]);
  } else {
    // Sometimes only file is present, or URL with no :line
    file = loc;
  }

  const shortFile = file ? shortFilename(file) : undefined;

  // Normalize noisy "Object." prefix
  fn = fn?.replace(/^Object\./, "");

  return { raw: line, fn, file, shortFile, line: lineNum, column: colNum };
}

function isIgnored(line: string, patterns: (string | RegExp)[]) {
  return patterns.some(p =>
    typeof p === "string" ? line.includes(p) : p.test(line)
  );
}

/** Parse a stack string into frames (unfiltered). */
export function parseStack(stack?: string): StackFrame[] {
  if (!stack) return [];
  return stack
    .split("\n")
    .map(l => l.trim())
    .filter(l => l.startsWith("at ")) // keep only frames
    .map(parseFrame);
}

/** Format frames into a human-friendly breadcrumb string. */
export function formatBreadcrumbs(
  frames: StackFrame[],
  opts: Pick<BreadcrumbOptions, "maxDepth" | "includeLocationInText"> = {}
): string {
  const { maxDepth = 5, includeLocationInText = true } = opts;
  const limited = frames.slice(0, maxDepth);

  const parts = limited.map(f => {
    const name = f.fn ?? "(anonymous)";
    if (!includeLocationInText) return name;
    let loc: string | undefined;
    if (f.shortFile) {
      loc = f.line ? `${f.shortFile}:${f.line}` : f.shortFile;
    } else {
      loc = undefined;
    }
    return loc ? `${name} (${gray(loc)})` : name;
  });

  return parts.join("\n\t→ ");
}

/**
 * Generate stacktrace breadcrumbs
 * - parses stack
 * - filters out common/internal frames and custom patterns
 * - (optionally) dedupes consecutive frames
 * - returns both formatted string and structured frames
 */
export function stackToBreadcrumbs(
  stack?: string,
  options: BreadcrumbOptions = {}
): { text: string; frames: StackFrame[] } {
  const {
    maxDepth = 5,
    dedupeConsecutive = true,
    filterCommonInternals = true,
    ignorePatterns = [],
    includeLocationInText = true
  } = options;

  const patterns = [
    ...(filterCommonInternals ? DEFAULT_IGNORE : []),
    ...ignorePatterns
  ];

  // 1) Parse
  let frames = parseStack(stack);

  // 2) Filter
  frames = frames.filter(f => !isIgnored(f.raw, patterns));

  // 3) Dedupe consecutive identical frames
  if (dedupeConsecutive && frames.length > 1) {
    const deduped: StackFrame[] = [frames[0]];
    for (let i = 1; i < frames.length; i++) {
      const a = deduped.at(-1);
      const b = frames[i];
      const same =
        a?.fn === b.fn && a?.file === b.file && a?.line === b.line && a?.column === b.column;
      if (!same) deduped.push(b);
    }
    frames = deduped;
  }

  // 4) Format
  const text = formatBreadcrumbs(frames, { maxDepth, includeLocationInText });

  return { text, frames };
}
